import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, Icon, stitchTooltipSlotProps } from '../ProjectOverview/parts/stitch';
import { TEKST, stilleKopSx, stilleLinkSx } from './rustigeKop';

export const bestandsIcoon = (naam = '') => {
  const ext = naam.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls'].includes(ext)) return 'table_view';
  if (['pptx', 'ppt'].includes(ext)) return 'co_present';
  if (['docx', 'doc'].includes(ext)) return 'description';
  if (ext === 'pdf') return 'picture_as_pdf';
  return 'draft';
};

// Soort document bij het uploaden (wiki 26 sep: "meteen aangeven wat voor bestand
// het is — Script, Briefing, …"). Het bestaande begrip daarvoor is de fase: de
// soort bepaalt in welke fase het document komt (zelfde indeling als het raden op
// bestandsnaam in de engine, FASE_KEYWORDS). null = de fase waarin je nu staat.
export const DOC_SOORTEN = [
  { label: 'Briefing', fase: 'opstartfase' },
  { label: 'Offerte', fase: 'offertefase' },
  { label: 'Concept', fase: 'concept' },
  { label: 'Script / storyboard', fase: 'concept' },
  { label: 'Draaiboek / planning', fase: 'preproductie' },
  { label: 'Callsheet / quitclaim', fase: 'productie' },
  { label: 'Feedback / review', fase: 'postproductie' },
  { label: 'Overig — bij deze fase', fase: null },
];
export const FASE_NAMEN = {
  opstartfase: 'Opstart & briefing', offertefase: 'Offerte', concept: 'Concept',
  preproductie: 'Preproductie', productie: 'Productie', postproductie: 'Postproductie',
  afronding: 'Afronding', evaluatie: 'Evaluatie',
};

/**
 * Documenten van deze fase: het íngevulde document (concept, script, draaiboek)
 * staat voorop; de templates zijn alleen het startpunt en verdwijnen achter een
 * linkje zodra er een echt document is geüpload.
 */
function FaseTemplates({ fase, projectRecId, projectNumber }) {
  const [templates, setTemplates] = useState(null);
  const [documenten, setDocumenten] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toonTemplates, setToonTemplates] = useState(false);
  const templateUploadRef = useRef(null);
  const docUploadRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Gekozen bestand wacht op "wat is dit?" — daarna pas uploaden.
  const [wachtend, setWachtend] = useState(null);
  const uploadKnopRef = useRef(null);

  const laad = useCallback(async () => {
    try {
      const [tr, dr] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/flow-templates?fase=${encodeURIComponent(fase)}`),
        projectRecId
          ? authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents?fase=${encodeURIComponent(fase)}`)
          : Promise.resolve(null),
      ]);
      setTemplates(tr?.ok ? (await tr.json())?.templates || [] : []);
      setDocumenten(dr?.ok ? (await dr.json())?.documenten || [] : []);
    } catch { setTemplates([]); setDocumenten([]); }
  }, [fase, projectRecId]);
  useEffect(() => { setTemplates(null); setDocumenten(null); setToonTemplates(false); laad(); }, [laad]);

  // Nieuw tabblad i.p.v. downloaden: een PDF bekijk je meteen, Office-bestanden
  // downloadt de browser alsnog vanzelf.
  const downloadVia = async (url) => {
    const r = await authFetch(url);
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    const d = await r.json();
    window.open(d.url, '_blank', 'noopener');
  };

  const uploadDocument = async (file, doelFase = fase) => {
    if (!file || uploading || !projectRecId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fase', doelFase || fase);
      fd.append('naam', file.name.replace(/\.[^.]+$/, ''));
      if (projectNumber) fd.append('projectNumber', projectNumber);
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json().catch(() => ({}));
      const waar = d.fase && d.fase !== fase ? `bij ${FASE_NAMEN[d.fase] || d.fase}` : 'bij deze fase';
      if (d.bestond_al) toast(`Dit document stond al ${waar} — niet nog een keer bewaard`, { icon: 'ℹ️' });
      else toast.success(`Document bewaard ${waar}`);
      await laad();
    } catch (e) { toast.error(`Document uploaden mislukt: ${e.message}`); }
    finally { setUploading(false); }
  };

  const uploadTemplate = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('naam', file.name.replace(/\.[^.]+$/, ''));
      fd.append('fase', fase);
      const r = await authFetch(`${BACKEND_URL}/Api/flow-templates`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      toast.success(d.vervangen ? 'Template vervangen door nieuwe versie' : 'Template toegevoegd aan deze fase');
      await laad();
    } catch (e) { toast.error(`Template uploaden mislukt: ${e.message}`); }
    finally { setUploading(false); }
  };

  const verwijderTemplate = async (t) => {
    if (!(await bevestig({ titel: `Template "${t.naam}" uit deze fase verwijderen?`, tekst: 'Dit geldt voor alle projecten.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    setBusyId(`tpl-${t.id}`);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/flow-templates/${t.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await laad();
      toast.success('Template verwijderd uit deze fase');
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
    finally { setBusyId(null); }
  };

  const verwijderDocument = async (doc) => {
    if (!(await bevestig({ titel: `Document "${doc.naam}" uit deze fase verwijderen?`, tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    setBusyId(`doc-${doc.id}`);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents/${doc.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await laad();
      toast.success('Document verwijderd uit deze fase');
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
    finally { setBusyId(null); }
  };

  // Documenten en templates als stille links (icoon + naam), geen grijze tegels.
  const chip = (icoon, label, onClick, extra = null, key) => (
    <Box key={key} component="span" onClick={onClick} sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
      fontFamily: T.fontBody, fontSize: TEKST, color: T.text,
      '&:hover': { textDecoration: 'underline' },
      '& .doc-extra': { opacity: 0, transition: 'opacity 0.15s ease' },
      '&:hover .doc-extra': { opacity: 1 },
    }}>
      <Icon name={icoon} size={16} sx={{ color: T.outline }} />
      {label}
      {extra && <Box component="span" className="doc-extra" sx={{ display: 'inline-flex', gap: 0.25 }}>{extra}</Box>}
    </Box>
  );

  const laden = templates === null || documenten === null;
  const heeftDocumenten = (documenten || []).length > 0;
  const aantalTemplates = (templates || []).length;
  const scheiding = <Box component="span" aria-hidden sx={{ color: T.outlineVariant }}>·</Box>;

  return (
    <Box className="zone-deel" sx={{ mb: 2 }}>
      <input ref={docUploadRef} type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setWachtend(f); }} />
      <Menu anchorEl={uploadKnopRef.current} open={!!wachtend} onClose={() => setWachtend(null)}
        slotProps={{ paper: { sx: { borderRadius: '0.6rem', minWidth: 220 } } }}>
        <Typography sx={{ px: 2, pt: 0.5, pb: 1, fontFamily: T.fontBody, fontSize: TEKST, color: T.outline, maxWidth: 280 }} noWrap>
          Wat is {wachtend?.name || 'dit'}?
        </Typography>
        {DOC_SOORTEN.map((soort) => (
          <MenuItem key={soort.label} dense sx={{ fontFamily: T.fontBody, fontSize: '0.8rem' }}
            onClick={() => { const f = wachtend; setWachtend(null); uploadDocument(f, soort.fase || fase); }}>
            {soort.label}
            {soort.fase && soort.fase !== fase && (
              <Box component="span" sx={{ ml: 'auto', pl: 2, color: T.outlineVariant, fontSize: '0.72rem' }}>
                {FASE_NAMEN[soort.fase]}
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
      {/* Eén regel: kop · (leeg) · uploaden · templates — lege secties blijven stil */}
      <Box className="zone-kop" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={stilleKopSx}>{heeftDocumenten || laden ? 'Documenten' : 'Nog geen documenten'}</Typography>
        {laden ? (
          <CircularProgress size={12} sx={{ color: T.outlineVariant }} />
        ) : (
          <>
            {scheiding}
            <Tooltip title="Upload het ingevulde document (concept/script/draaiboek)" slotProps={stitchTooltipSlotProps}>
              <Box component="button" type="button" disabled={uploading} ref={uploadKnopRef}
                onClick={() => docUploadRef.current?.click()} sx={stilleLinkSx}>
                {uploading && <CircularProgress size={12} />}
                uploaden
              </Box>
            </Tooltip>
            {scheiding}
            {/* Templates: startpunt — standaard ingeklapt (Joris, 25 aug) */}
            <Box component="button" type="button" onClick={() => setToonTemplates((v) => !v)} sx={stilleLinkSx}
              aria-expanded={toonTemplates}>
              {toonTemplates ? 'templates verbergen'
                : aantalTemplates ? `${aantalTemplates} template${aantalTemplates === 1 ? '' : 's'}` : 'template toevoegen'}
            </Box>
          </>
        )}
      </Box>

      {!laden && heeftDocumenten && (
        <Box sx={{ display: 'flex', columnGap: 2.5, rowGap: 1, flexWrap: 'wrap', mt: 1 }}>
          {documenten.map((doc) => (
            <Tooltip key={doc.id} slotProps={stitchTooltipSlotProps}
              title={`${doc.bestandsnaam}${doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}${doc.created_at ? ` · ${doc.created_at.slice(0, 10)}` : ''} — klik om te openen`}>
              {chip(bestandsIcoon(doc.bestandsnaam), doc.naam,
                () => busyId !== `doc-${doc.id}` && downloadVia(
                  `${BACKEND_URL}/Api/project-flow/${projectRecId}/documents/${doc.id}/download`, doc.bestandsnaam,
                ).catch((e) => toast.error(`Download mislukt: ${e.message}`)),
                <Icon name="close" size={13} sx={{ color: T.outlineVariant, '&:hover': { color: T.error } }}
                  onClick={(e) => { e.stopPropagation(); verwijderDocument(doc); }} />,
                doc.id)}
            </Tooltip>
          ))}
        </Box>
      )}

      {!laden && toonTemplates && (
        <Box sx={{ display: 'flex', columnGap: 2.5, rowGap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
          {aantalTemplates > 0 && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: TEKST, color: T.outline, width: '100%' }}>
              Templates zijn het startpunt — upload daarna het ingevulde document.
            </Typography>
          )}
          {(templates || []).map((t) => (
            <Tooltip key={`t-${t.id}`} slotProps={stitchTooltipSlotProps}
              title={`Template · ${t.bestandsnaam}${t.categorie ? ` · ${t.categorie}` : ''} — klik om te downloaden`}>
              {chip(bestandsIcoon(t.bestandsnaam), t.naam,
                () => downloadVia(`${BACKEND_URL}/Api/flow-templates/${t.id}/download`, t.bestandsnaam)
                  .catch((e) => toast.error(`Download mislukt: ${e.message}`)),
                <Icon name="close" size={13} sx={{ color: T.outlineVariant, '&:hover': { color: T.error } }}
                  onClick={(e) => { e.stopPropagation(); busyId !== `tpl-${t.id}` && verwijderTemplate(t); }} />,
                `t-${t.id}`)}
            </Tooltip>
          ))}
          <input ref={templateUploadRef} type="file" hidden onChange={(e) => { uploadTemplate(e.target.files?.[0]); e.target.value = ''; }} />
          <Tooltip title="Template toevoegen (zelfde naam = nieuwe versie)" slotProps={stitchTooltipSlotProps}>
            <Box component="button" type="button" disabled={uploading}
              onClick={() => templateUploadRef.current?.click()} sx={stilleLinkSx}>
              + template
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

export default FaseTemplates;
